import { Fragment, useState } from 'react'
import { DIETARY_LABEL, DIETARY_OPTIONS } from '../../dietary'
import type {
  AdminInvite,
  AdminInviteInput,
  AdminPersonInput,
  DietaryRequirement,
  InviteType,
  RsvpResponse,
} from '../../types'

interface InviteEditorProps {
  /** The invite being edited, or null when creating a new one. */
  invite: AdminInvite | null
  saving: boolean
  error: string | null
  onSave: (input: AdminInviteInput) => void
  onCancel: () => void
}

/** A row being edited. `person_id` is absent for somebody newly added. */
type Row = AdminPersonInput

function rowsFrom(invite: AdminInvite | null): Row[] {
  if (!invite) return [blankRow()]
  return invite.people.map((p) => ({
    person_id: p.person_id,
    name: p.name,
    invite_type: p.invite_type,
    is_child: p.is_child,
    response: p.response,
    dietary: [...p.dietary],
  }))
}

function blankRow(): Row {
  return { name: '', invite_type: 'day', is_child: false, response: 'waiting', dietary: [] }
}

/**
 * Edits one invite as a whole.
 *
 * What is submitted is the invite's desired end state, not a list of changes:
 * rows keeping their `person_id` are updated in place - so their response
 * survives a rename - and anyone deleted here is simply left out, which is how
 * the server knows to remove them.
 */
export function InviteEditor({ invite, saving, error, onSave, onCancel }: InviteEditorProps) {
  const [code, setCode] = useState(invite?.code ?? '')
  const [notes, setNotes] = useState(invite?.notes ?? '')
  const [rows, setRows] = useState<Row[]>(() => rowsFrom(invite))

  const creating = invite === null

  function update(index: number, patch: Partial<Row>) {
    setRows((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)))
  }

  function remove(index: number) {
    setRows((current) => current.filter((_, i) => i !== index))
  }

  function toggleDietary(index: number, requirement: DietaryRequirement) {
    setRows((current) =>
      current.map((row, i) => {
        if (i !== index) return row
        const dietary = row.dietary.includes(requirement)
          ? row.dietary.filter((r) => r !== requirement)
          : [...row.dietary, requirement]
        return { ...row, dietary }
      }),
    )
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (saving) return

    const people = rows
      .map((row) => ({ ...row, name: row.name.trim() }))
      .filter((row) => row.name !== '')
    if (people.length === 0) return

    onSave({ code: code.trim(), notes: notes.trim(), people })
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="border-claret/30 bg-parchment/60 rounded-lg border p-4 shadow-sm"
    >
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="invite-code" className="text-ink-soft block text-xs tracking-widest uppercase">
            Code
          </label>
          <input
            id="invite-code"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            placeholder={creating ? 'leave blank to generate' : ''}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            className="border-ink/20 focus:border-claret mt-1 w-56 rounded-md border bg-white/60 px-3 py-2 font-mono tracking-[0.2em] uppercase"
          />
        </div>
        {creating && (
          <p className="text-ink-soft pb-2 text-xs">
            Blank generates one, avoiding easily confused characters.
          </p>
        )}
      </div>

      <table className="mt-4 w-full text-sm">
        <thead>
          <tr className="text-ink-soft text-left text-xs tracking-widest uppercase">
            <th scope="col" className="pb-2 font-normal">
              Name
            </th>
            <th scope="col" className="pb-2 font-normal">
              Invited to
            </th>
            <th scope="col" className="pb-2 font-normal">
              Response
            </th>
            <th scope="col" className="pb-2 font-normal">
              Child
            </th>
            <th scope="col" className="pb-2">
              <span className="sr-only">Remove</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <Fragment key={row.person_id ?? `new-${index}`}>
            <tr className="border-ink/10 border-t">
              <td className="py-2 pr-2">
                <input
                  aria-label={`Name of guest ${index + 1}`}
                  value={row.name}
                  onChange={(event) => update(index, { name: event.target.value })}
                  className="border-ink/20 focus:border-claret w-full rounded-md border bg-white/60 px-2 py-1.5"
                />
              </td>
              <td className="py-2 pr-2">
                <select
                  aria-label={`What ${row.name || `guest ${index + 1}`} is invited to`}
                  value={row.invite_type}
                  onChange={(event) =>
                    update(index, { invite_type: event.target.value as InviteType })
                  }
                  className="border-ink/20 focus:border-claret rounded-md border bg-white/60 px-2 py-1.5"
                >
                  <option value="day">All day</option>
                  <option value="evening">Evening</option>
                </select>
              </td>
              <td className="py-2 pr-2">
                <select
                  aria-label={`Response for ${row.name || `guest ${index + 1}`}`}
                  value={row.response}
                  onChange={(event) =>
                    update(index, { response: event.target.value as RsvpResponse })
                  }
                  className="border-ink/20 focus:border-claret rounded-md border bg-white/60 px-2 py-1.5"
                >
                  <option value="waiting">Waiting</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </td>
              <td className="py-2 pr-2">
                <label className="flex cursor-pointer items-center gap-1.5 text-xs">
                  <input
                    type="checkbox"
                    // Named for the guest, since "Child" alone appears once per
                    // row and says nothing about which row it belongs to.
                    aria-label={`${row.name || `Guest ${index + 1}`} is a child`}
                    checked={row.is_child}
                    onChange={(event) => update(index, { is_child: event.target.checked })}
                    className="accent-claret"
                  />
                  Child
                </label>
              </td>
              <td className="py-2 text-right align-top">
                <button
                  type="button"
                  onClick={() => remove(index)}
                  disabled={rows.length === 1}
                  className="text-ink-soft hover:text-claret text-xs tracking-widest uppercase disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Remove
                </button>
              </td>
            </tr>
            {/* Dietary requirements get their own row: there are three of them
                and they would squeeze the name field to nothing inline. */}
            <tr>
              <td colSpan={5} className="pb-3">
                <div className="text-ink-soft flex flex-wrap items-center gap-3 text-xs">
                  <span className="tracking-widest uppercase">Dietary</span>
                  {DIETARY_OPTIONS.map((requirement) => (
                    <label key={requirement} className="flex cursor-pointer items-center gap-1.5">
                      <input
                        type="checkbox"
                        aria-label={`${row.name || `Guest ${index + 1}`} is ${DIETARY_LABEL[requirement].toLowerCase()}`}
                        checked={row.dietary.includes(requirement)}
                        onChange={() => toggleDietary(index, requirement)}
                        className="accent-claret"
                      />
                      {DIETARY_LABEL[requirement]}
                    </label>
                  ))}
                </div>
              </td>
            </tr>
            </Fragment>
          ))}
        </tbody>
      </table>

      <button
        type="button"
        onClick={() => setRows((current) => [...current, blankRow()])}
        className="border-ink/20 hover:border-claret hover:text-claret mt-3 rounded-md border px-3 py-1.5 text-xs tracking-widest uppercase transition-colors"
      >
        Add guest
      </button>

      <div className="mt-4">
        <label htmlFor="invite-notes" className="text-ink-soft block text-xs tracking-widest uppercase">
          Notes
        </label>
        {/* Said plainly, because the whole value of the field is trusting it. */}
        <p className="text-ink-soft mt-0.5 text-xs">
          Only ever visible here. Guests never see this, and it is not in anything
          the site sends them.
        </p>
        <textarea
          id="invite-notes"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          rows={2}
          placeholder="Anything worth remembering about this invite"
          className="border-ink/20 focus:border-claret mt-1.5 w-full rounded-md border bg-white/60 px-3 py-2 text-sm"
        />
      </div>

      {error && (
        <p role="alert" className="text-claret mt-4 text-sm">
          {error}
        </p>
      )}

      <div className="mt-4 flex gap-3">
        <button
          type="submit"
          disabled={saving}
          className="bg-claret hover:bg-claret-dark rounded-md px-5 py-2 text-sm tracking-widest text-white uppercase transition-colors disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? 'Saving…' : creating ? 'Create invite' : 'Save changes'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="border-ink/20 hover:border-claret rounded-md border px-5 py-2 text-sm tracking-widest uppercase transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}
